import {createElement, Component, PropTypes} from 'rlink-react';
import ReactDOM from 'react-dom';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import BezierEasing from 'bezier-easing';
import {Picture} from 'rlink-repos/content-components';
import {injectIntl, defineMessages} from 'react-intl';

const INTL_MESSAGES = defineMessages({
  previous: {
    id: 'ui.text.info.PREVIOUS_SLIDE',
    defaultMessage: 'Previous',
    description: 'Alternative text for an arrow image pointing left (meaning previous) appearing on the image carousel.',
  },
  next: {
    id: 'ui.text.info.NEXT_SLIDE',
    defaultMessage: 'Next',
    description: 'Alternative text for an arrow image pointing right (meaning next) appearing on the image carousel.',
  },
});

/* function visibleBounds (offset, viewportSize) {
 *   return Math.max(0, Math.min(viewportSize, offset));
 * }
 *
 *
 * export class EventEmitter {
 *   constructor () {
 *     this.listeners = {};
 *   }
 *   addListener (eventName, handler) {
 *     if (!(eventName in this.listeners)) {
 *       this.listeners[eventName] = [];
 *     }
 *     this.listeners[eventName].push(handler);
 *     return handler;
 *   }
 *   removeListener (eventName, handler) {
 *     if ( eventName in this.listeners ) {
 *       removeAllFrom(handler, this.listeners[eventName]);
 *     }
 *   }
 *   emit (eventName, ...eventArgs) {
 *     if ( eventName in this.listeners ) {
 *       let handlers = this.listeners[eventName];
 *       for ( let handler of handlers ) {
 *         handler(...eventArgs);
 *       }
 *     }
 *   }
 * }
 *
 *
 * class VisibilityUpdater extends EventEmitter {
 *   constructor (element) {
 *     super();
 *     this.element = element;
 *   }
 *   mount () {
 *     this.visibilityChecker = () => {
 *       if (!this._hasNextFrameHandler) {
 *         requestAnimationFrame(ts => {
 *           let bounds = this.element.getBoundingClientRect();
 *
 *           let visibleHeight = visibleBounds(bounds.bottom, window.innerHeight) - visibleBounds(bounds.top, window.innerHeight);
 *           if (visibleHeight > 0) {
 *             if (!this.elementVisible) {
 *               this.elementVisible = true;
 *               this.emit('becameVisible');
 *               this.emit('visibilityChange', {visible: true});
 *             }
 *           } else {
 *             if (this.elementVisible) {
 *               this.elementVisible = false;
 *               this.emit('becameHidden');
 *               this.emit('visibilityChange', {visible: false});
 *             }
 *           }
 *           this._hasNextFrameHandler = false;
 *         });
 *         this._hasNextFrameHandler = true;
 *       }
 *     };
 *     this.visibilityChecker();
 *     window.addEventListener('scroll', this.visibilityChecker);
 *     window.addEventListener('resize', this.visibilityChecker);
 *   }
 *   unmount () {
 *     window.removeEventListener('scroll', this.visibilityChecker);
 *     window.removeEventListener('resize', this.visibilityChecker);
 *   }
 * }
 * */

class CanvasStrip {
  constructor ({
      canvasElement,
      canvasWidth = null,
      canvasHeight = null,
      autoFitCanvas = true,
      renderPosition,
      stripSize = Infinity,
      loop = false,
      initialPosition = 0.0,
      animationEasing = x => x,
      animationAvgVelocity = null,
      animationDuration = null,
    }) {

    this.canvas = canvasElement;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.autoFitCanvas = autoFitCanvas;
    this.renderPosition = renderPosition;
    this.stripSize = stripSize;
    this.loop = loop;
    this.position = initialPosition;
    this.targetPosition = null;
    this.holdPosition = null;
    this.easing = animationEasing;
    this.animationDuration = animationDuration;
    this.animationAvgVelocity = animationAvgVelocity;
    this.ctx = this.canvas.getContext('2d');
    this._animatingToTarget = null;
    this.draw();
  }

  destroy () {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
    }
    window.removeEventListener('resize', this._boundSizeHandler);
  }

  set canvasWidth (width) {
    if (width !== null) {
      this.canvas.width = width;
    }
  }

  set canvasHeight (height) {
    if (height !== null) {
      this.canvas.height = height;
    }
  }

  set autoFitCanvas (enabled) {
    this._boundSizeHandler = () => this.updateSize();
    if (enabled) {
      window.addEventListener('resize', this._boundSizeHandler);
      this.updateSize();
    } else {
      window.removeEventListener('resize', this._boundSizeHandler);
    }
  }

  updateSize () {
    this.canvas.width = this.canvas.parentNode.offsetWidth;
    this.canvas.height = this.canvas.parentNode.offsetHeight;
    this.draw();
  }


  set targetPosition (position) {
    this._targetPosition = position;
    this.draw();

  }
  get targetPosition () {
    return this._targetPosition;
  }

  set holdPosition (position) {
    if (this.loop) {
      if (position < 0) {
        this.holdPosition = this.stripSize + position;
        return;
      }
      if (position > this.stripSize) {
        this.holdPosition = position - this.stripSize;
        return;
      }
    }
    this._holdPosition = position;
    this.draw();
  }

  get holdPosition () {
    return this._holdPosition;
  }

  animDurationForPositionChange (startPos, endPos) {
    if (this.animationDuration !== null) {
      // constant animation duration
      return this.animationDuration;
    } else {
      // average velocity based
      let distance = Math.min(
        this.euclidianDistance(startPos, endPos),
        this.cylindricalDistance(startPos, endPos)
      );
      return distance / this.animationAvgVelocity;
    }
  }

  euclidianDistance (startPos, endPos) {
    return Math.abs(endPos - startPos);
  }

  cylindricalDistance (startPos, endPos) {
    return Math.min(this.stripSize - startPos, this.stripSize - endPos) + Math.min(startPos, endPos);
  }

  tweenPosition (startPos, endPos, progress) {
    let euclidianDistance = this.euclidianDistance(startPos, endPos);
    let cylindricalDistance = this.cylindricalDistance(startPos, endPos);

    if (cylindricalDistance < euclidianDistance) {
      let distanceToEnd = this.stripSize - startPos;
      let distanceToStart = startPos;
      if (distanceToEnd < distanceToStart) {
        // tween forward
        let toEndPortion = distanceToEnd / cylindricalDistance;
        if (progress < toEndPortion) {
          return startPos + cylindricalDistance * progress;
        } else {
          return cylindricalDistance * progress - distanceToEnd;
        }
      } else {
        // tween backward
        let toStartPortion = distanceToStart / cylindricalDistance;
        if (progress <= toStartPortion) {
          return startPos - cylindricalDistance * progress;
        } else {
          return this.stripSize - (progress * cylindricalDistance - distanceToStart);
        }
      }

    } else {
      return startPos + (endPos - startPos) * progress;
    }

  }

  draw () {
    if (!this._drawPending) {
      this._drawPending = true;
      this._raf = requestAnimationFrame(ts => {
        this._drawPending = false;

        // holdPosition always overrides animation
        if (this._holdPosition !== null) {
          this._animatingToTarget = null;
          this.position = this._holdPosition;
        } else if (this._targetPosition !== null
            && this._targetPosition !== this.position) {

          if ( this._animatingToTarget !== this._targetPosition) {
            this._animStartTs = ts;
            this._animStartPosition = this.position;
            this._animatingToTarget = this._targetPosition;

          }
          let animDuration = this.animDurationForPositionChange(
            this._animStartPosition,
            this._targetPosition);
          let animProgress = (ts - this._animStartTs) / animDuration;
          if (animProgress >= 1.0) {
            this.position = this._targetPosition;
            this._animatingToTarget = null;
          } else {
            let easedPositionAnimDelta = this.easing(animProgress);
            let fullAnimPosDelta = this._targetPosition - this._animStartPosition;

            this.position = this.tweenPosition(this._animStartPosition, this._targetPosition, easedPositionAnimDelta);
          }
        }

        this.renderPosition(this.position, this.ctx);
        if (this._animatingToTarget !== null && this._animatingToTarget !== this.position) {
          this.draw();
        }
      });
    }
  }

}

export const Slider = injectIntl(
  class Slider extends Component {
    constructor (props) {
      super(props);

      this.state = {
        currentSlide: props.startingSlide,
        positionAtHoldStart: null,
      };
      this._drawCanvas = ev => {
        this.canvasStrip.draw();
        if (ev.name === 'load') {
          ev.target.removeEventListener(
            'load', this._drawCanvas);
          ev.target.removeEventListener(
            'readystatechange', this._drawCanvas);
        }

      };
    }

    slideElement (idx) {
      return this.refs.slides.children[idx];
    }
    slideImage (idx) {
      let img = this.slideElement(idx).querySelector('img');
      return img;
    }
    get canvas () {
      return this.refs.canvas;
    }

    get container () {
      return ReactDOM.findDOMNode(this);
    }
    autoSlideNext () {
      if (this.props.autoSlide && this.state.positionAtHoldStart === null) {
        clearTimeout(this._autoSlideTimeout);
        this._autoSlideTimeout = setTimeout(() => {
          this.nextSlide();
        }, this.props.autoSlide);
      } else {
        clearTimeout(this._autoSlideTimeout);
      }
    }

    componentDidMount () {
      this.autoSlideNext();
      this.canvasStrip = new CanvasStrip({
        canvasElement: this.canvas,
        autoFitCanvas: true,
        renderPosition: (pos, ctx) => this.renderPosition(pos, ctx),
        stripSize: this.props.children.length,
        loop: true,
        initialPosition: this.props.startingSlide,
        animationEasing: this.easing,
        animationAvgVelocity: 1.0 / this.props.slideAnimDuration,
      });
      this.canvasStrip.targetPosition = this.props.startingSlide;
    }

    componentDidUpdate (prevProps, prevState) {
      if (prevState.currentSlide !== this.state.currentSlide) {
        this.canvasStrip.targetPosition = this.state.currentSlide;
        this.autoSlideNext();
      } else if (prevState.positionAtHoldStart
        !== this.state.positionAtHoldStart) {
        this.autoSlideNext();
      }
    }

    componentWillUnmount () {
      clearTimeout(this._autoSlideTimeout);
      cancelAnimationFrame(this._raf);
      if (this.canvasStrip) {
        this.canvasStrip.destroy();
      }

      for (let i = 0; i < this.props.children.length; i++) {
        let image = this.slideImage(i);
        image.removeEventListener('load', this._drawCanvas);
        image.removeEventListener('readystatechange', this._drawCanvas);
      }

    }

    reRenderOnLoad (images) {
      if (this._reRenderImages ) {
        for (let image of this._reRenderImages) {
          image.removeEventListener('load', this._drawCanvas);
          image.removeEventListener('readystatechange', this._drawCanvas);
        }
      }
      let reRenderImages = [];
      for (let image of images) {
        if (!image.complete) {
          reRenderImages.push(image);
          image.addEventListener('load', this._drawCanvas);
          image.addEventListener('readystatechange', this._drawCanvas);
        }
      }
      this._reRenderImages = images;
    }

    renderPosition (position, ctx) {
      let leftSlideIdx = Math.floor(position);
      let rightSlideIdx = this.nextSlideIdx(leftSlideIdx);

      let leftSlideImg = this.slideImage(leftSlideIdx);
      let rightSlideImg = this.slideImage(rightSlideIdx);

      this.reRenderOnLoad([leftSlideImg, rightSlideImg]);

      let offset = position - leftSlideIdx;

      let canvasWidth = ctx.canvas.width;
      let canvasHeight = ctx.canvas.height;
      let imageWidth = (leftSlideImg.naturalWidth > canvasWidth) ? canvasWidth : leftSlideImg.naturalWidth;
      let imageHeight = (leftSlideImg.naturalHeight > canvasHeight) ? canvasHeight : leftSlideImg.naturalHeight;
      ctx.drawImage(leftSlideImg,
                    leftSlideImg.naturalWidth * offset, 0, //sx sy
                    leftSlideImg.naturalWidth * (1.0 - offset), leftSlideImg.naturalHeight, //sw sh
                    0, 0,  //dx dy
                    canvasWidth * (1.0 - offset), canvasHeight //dw dh
      );

      imageWidth = (rightSlideImg.naturalWidth > canvasWidth) ? canvasWidth : rightSlideImg.naturalWidth;
      imageHeight = (rightSlideImg.naturalHeight > canvasHeight) ? canvasHeight : rightSlideImg.naturalHeight;

      if (offset !== 0) {

        ctx.drawImage(rightSlideImg,
                      0, 0, //sx sy
                      rightSlideImg.naturalWidth * offset, rightSlideImg.naturalHeight, //sw sh
                      canvasWidth * (1.0 - offset), 0,  //dx dy
                      canvasWidth * (offset), canvasHeight // dw dh
        );
      }
    }


    get easing () {
      return new BezierEasing(0.4, 0.0, 0.2, 1);
    }



    currentSlideTitle () {
      if (typeof (this.props.children[this.state.currentSlide]) !== 'undefined') {
        return this.props.children[this.state.currentSlide].props.title;
      }

      return '';
    }

    get lastSlide () {
      return this.props.children.length - 1;
    }



    nextSlideIdx (fromSlide = this.state.currentSlide) {
      return fromSlide === this.lastSlide
           ? (this.props.loop ? 0 : this.lastSlide)
           : fromSlide + 1;
    }
    nextSlide () {
      this.setState({
        currentSlide: this.nextSlideIdx(),
      });
    }
    prevSlideIdx (fromSlide = this.state.currentSlide) {
      return fromSlide === 0
           ? (this.props.loop ? this.lastSlide : 0)
           : fromSlide - 1;
    }
    prevSlide () {
      this.setState({
        currentSlide: this.prevSlideIdx(),
      });
    }
    setTouchStartPoints (ev) {
      let touches = this.state.touches || {};
      for (let touch of Array.from(ev.touches)) {
        touches[touch.identifier] = touch;
      }
      this.setState({touches});
    }

    clearTouchStartPoints (ev) {
      let touches = this.state.touches || {};
      for (let touch of Array.from(ev.changedTouches)) {
        delete touches[touch.identifier];
      }
      if (Object.keys(touches).length > 0) {
        this.setState({touches});
      } else {
        this.setState({positionAtHoldStart: null});
        this.canvasStrip.holdPosition = null;
      }
    }

    handleTouchMove (ev) {
      ev.preventDefault();
      let changedTouches = Array.from(ev.changedTouches);
      if (!this._handleNextMove) {
        this._handleNextMove = true;
        this._raf = requestAnimationFrame(ts => {
          this._handleNextMove = false;
          let largestMove = 0;
          for (let touch of changedTouches) {
            if (touch.identifier in this.state.touches) {
              let move = this.state.touches[touch.identifier].clientX - touch.clientX;
              if (Math.abs(move) > Math.abs(largestMove)) {
                largestMove = move;
              }
            }

          }

          if (Math.abs(largestMove) > this.props.touchDragThreshold) {
            let initialPosition = this.state.positionAtHoldStart;
            let initialSlideIdx = this.state.slideIdxAtHoldStart;
            if (initialPosition === null) {
              initialPosition = this.canvasStrip.position;
              initialSlideIdx = this.state.currentSlide;
              this.setState({
                positionAtHoldStart: initialPosition,
                slideIdxAtHoldStart: initialSlideIdx,
              });
            }
            let positionChange = largestMove / this.canvasStrip.canvas.width;
            this.updateCurrentSlide(positionChange, initialSlideIdx);

            this.canvasStrip.holdPosition = initialPosition + positionChange;
          }

        });
      }


    }

    updateCurrentSlide (positionChange, initialSlideIdx) {
      if (positionChange > 1.0) {
        this.updateCurrentSlide(positionChange - 1.0, this.nextSlideIdx(initialSlideIdx));
        return;
      }
      if (positionChange < -1.0) {
        this.updateCurrentSlide(positionChange + 1.0, this.prevSlideIdx(initialSlideIdx));
        return;
      }
      if (Math.abs(positionChange) > this.props.touchSwitchThreshold) {
        let currentSlide;
        if (positionChange <= 0) {
          currentSlide = this.prevSlideIdx(initialSlideIdx);
        } else {
          currentSlide = this.nextSlideIdx(initialSlideIdx);
        }
        this.setState({currentSlide});
      }
    }
    render () {
      return <div className={this.props.css.slider}
        onTouchStart={ev => this.setTouchStartPoints(ev)}
        onTouchEnd={ev => this.clearTouchStartPoints(ev)}
        onTouchCancel={ev => this.clearTouchStartPoints(ev)}
        onTouchMove={ev => this.handleTouchMove(ev)}>
  <div className={this.props.css.slides} ref="slides">
    {this.props.children}
  </div>
  <canvas ref="canvas" />
  <div className={this.props.css.caption}>
    <button className={this.props.css.prev} onClick={this.prevSlide.bind(this)}>
      <img src={this.props.arrowIcon} alt={this.props.intl.formatMessage(INTL_MESSAGES.previous)} />
    </button>
    <ReactCSSTransitionGroup
      transitionName="crossfade"
      transitionEnterTimeout={1000}
      transitionLeaveTimeout={1000}>
      <span key={this.state.currentSlide} className={this.props.css['caption-text']}>{this.currentSlideTitle()}</span>
    </ReactCSSTransitionGroup>
    <button className={this.props.css.next} onClick={this.nextSlide.bind(this)}>
      <img src={this.props.arrowIcon} alt={this.props.intl.formatMessage(INTL_MESSAGES.next)} />
    </button>
  </div>
      </div>;
    }
  }
);

Slider.defaultProps = {
  startingSlide: 0,
  autoSlide: 4000,
  slideAnimDuration: 1500,
  loop: true,
  touchDragThreshold: 10,
  touchSwitchThreshold: 0.2,
};

export default function Gallery (props) {
  return <Slider arrowIcon={props.arrowIcon} css={props.css}>
    {props.items.map(item => <GalleryImage
      css={props.css}
      key={item.imagePath}
      {... item} />)}
  </Slider>;
}



export function GalleryImage (props) {
  return <div className={props.css['gallery-item'] + (props.css[props.className] || '')}>
    <Picture
      src={props.src}
      imagePath={props.imagePath/*REMOVE ME*/}
      widths={props.widths}
      heights={props.heights}
      formats={props.formats} />
  </div>;
}



// WEBPACK FOOTER //
// ./apps/www.dawnofwar.com/src/components/image-carousel.jsx